// ERRORS-1: the ONE way the app tells someone a recording went wrong (docs/specs/errors-1.md
// §4). Renders a catalogue entry from `@scribe/core/errors` — title, plain-language cause,
// numbered steps, one primary action — plus "Copy diagnostic details" and, when bug reports
// are enabled, "Report this problem" prefilled with the recording + code.
//
// Used by RecordScreen (capture/stream failures), RecordingDetail (failed/stuck recordings)
// and RecordingsList rows (`variant="inline"`, headline only). Screens pass the handler for
// the primary action they can perform; actions with an app-wide meaning (`sign_in`, `reload`,
// `report`) have defaults here.
import { useEffect, useState, type ReactNode } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  getRecordingProblem,
  microphoneHelpSteps,
  type RecordingProblemCode,
} from '@scribe/core/errors';
import { useBugReport } from '../lib/bugReport';
import { collectDiagnostics, recordClientError } from '../lib/diagnostics';

export interface RecordingProblemProps {
  code: RecordingProblemCode | string;
  variant?: 'banner' | 'inline';
  recordingId?: string | null;
  /** Server `failure_detail` — included in copied diagnostics, never shown as the headline. */
  detail?: string | null;
  /** True once some audio is provably saved (the bridge acked a chunk): adds the "nothing is
   * lost" reassurance. Never inferred — see useStreamingSession's `captured`. */
  audioCaptured?: boolean;
  /** Performs the catalogue's primary action (retry, restart, …). Omitted → no button, unless
   * the action has an app-wide default (sign in, reload, report). */
  onPrimaryAction?: () => void;
  primaryPending?: boolean;
  /** Extra context-specific controls (e.g. RecordScreen's Restart, the retry options). */
  children?: ReactNode;
  /** Appended after the steps — e.g. "Start a new session to keep going." */
  footnote?: string;
}

export function RecordingProblem({
  code,
  variant = 'banner',
  recordingId,
  detail,
  audioCaptured,
  onPrimaryAction,
  primaryPending,
  children,
  footnote,
}: RecordingProblemProps) {
  const problem = getRecordingProblem(code);
  const bugReport = useBugReport();
  const [copied, setCopied] = useState(false);

  // Leave the code in the diagnostics trail, once per distinct problem shown.
  useEffect(() => {
    if (variant === 'banner') recordClientError({ code: problem.code });
  }, [problem.code, variant]);

  if (variant === 'inline') {
    return (
      <span className="problem-inline" title={problem.cause}>
        {problem.title}
      </span>
    );
  }

  const steps =
    problem.action === 'grant_microphone' ? microphoneHelpSteps(navigator.userAgent) : problem.steps;

  const openReport = bugReport
    ? () =>
        bugReport.open({
          recordingId: recordingId ?? null,
          failureCode: problem.code,
          area: problem.area,
          summary: problem.title,
        })
    : undefined;

  const primary: (() => void) | undefined =
    onPrimaryAction ??
    (problem.action === 'sign_in' || problem.action === 'reload'
      ? () => window.location.reload()
      : problem.action === 'report'
        ? openReport
        : undefined);
  // `report` as the primary action already IS the report button.
  const showSecondaryReport = !!openReport && problem.action !== 'report';

  async function copyDiagnostics() {
    const payload = {
      problem: problem.code,
      ...(detail ? { failure_detail: detail } : {}),
      ...(recordingId ? { recording_id: recordingId } : {}),
      ...collectDiagnostics(),
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="banner banner-danger problem" role="alert">
      <div className="problem-body">
        <p className="problem-title">
          <ExclamationTriangleIcon width={20} height={20} aria-hidden="true" />
          <strong>{problem.title}</strong>
        </p>
        <p>
          {problem.cause}
          {audioCaptured &&
            ' The audio captured before this happened has already been saved — nothing is lost.'}
        </p>
        <ol className="problem-steps">
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        {footnote && <p>{footnote}</p>}
        {children}
      </div>
      <div className="problem-actions">
        {primary && problem.actionLabel && (
          <button type="button" className="sm" disabled={primaryPending} onClick={primary}>
            {problem.actionLabel}
          </button>
        )}
        {showSecondaryReport && (
          <button type="button" className="ghost sm" onClick={openReport}>
            Report this problem
          </button>
        )}
        <button type="button" className="ghost sm" onClick={() => void copyDiagnostics()}>
          {copied ? 'Copied' : 'Copy diagnostic details'}
        </button>
      </div>
    </div>
  );
}
